// src/features/files/upload/useFileDrop.js
import { useCallback, useRef, useState } from 'react';

/** True for drags that carry files from the desktop, not in-app drags (e.g. moving a file to a folder). */
export const isFileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

/**
 * Drop handling for one area. Spread `dropProps` on the element; `dragActive`
 * is true while files are dragged over it. Only file drags are handled, so the
 * explorer's own drag-to-move keeps working inside the same area.
 */
export function useFileDrop({ onFiles, disabled = false }) {
  const [dragActive, setDragActive] = useState(false);
  // dragenter/dragleave fire for every child crossed, so count them instead of trusting one leave
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setDragActive(false);
  }, []);

  const onDragEnter = useCallback((event) => {
    if (disabled || !isFileDrag(event)) return;
    event.preventDefault();
    depth.current += 1;
    setDragActive(true);
  }, [disabled]);

  const onDragOver = useCallback((event) => {
    if (disabled || !isFileDrag(event)) return;
    // Without this the browser refuses the drop (and opens the file instead)
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [disabled]);

  const onDragLeave = useCallback((event) => {
    if (disabled || !isFileDrag(event)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragActive(false);
  }, [disabled]);

  const onDrop = useCallback((event) => {
    if (disabled || !isFileDrag(event)) return;
    event.preventDefault();
    // Nested drop areas: the innermost one takes the files
    event.stopPropagation();
    reset();
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) onFiles(files);
  }, [disabled, onFiles, reset]);

  return { dragActive, dropProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
